import React from 'react';
import { ShieldCheck, Phone, PhoneOff, Clock, AlertTriangle } from 'lucide-react';
import { LiberacaoSummary } from '../../utils/liberacoes';
import { cn } from '../../utils/cn';

/**
 * "Este visitante já está liberado" — the loudest thing on the Cadastrar Acesso screen.
 *
 * A resident can liberar someone in advance from the app, per unit, each with its own period and
 * its own intercom rule. None of that reached the portaria: the operator saw an ordinary visitor
 * and ran the ordinary interfonar-and-wait procedure, including for people the resident had
 * explicitly said not to call about.
 *
 * Three things decide what the operator does next, so those three are what this says, big:
 * WHICH unit, UNTIL WHEN, and INTERFONAR OR NOT. Everything else is detail.
 */

export interface LiberacoesBannerProps {
  liberacoes: LiberacaoSummary[];
  /** The unit the operator has picked in the form, if any — used to call out which one applies. */
  selectedUnitId?: string | null;
  darkMode?: boolean;
  className?: string;
}

const Badge: React.FC<{ tone: 'green' | 'amber' | 'red' | 'gray' | 'blue'; children: React.ReactNode; icon?: React.ReactNode }> = ({
  tone,
  children,
  icon,
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap',
      tone === 'green' && 'bg-emerald-500 text-white',
      tone === 'amber' && 'bg-amber-500 text-white',
      tone === 'red' && 'bg-red-500 text-white',
      tone === 'blue' && 'bg-blue-500 text-white',
      tone === 'gray' && 'bg-gray-400 text-white',
    )}
  >
    {icon}
    {children}
  </span>
);

export const LiberacoesBanner: React.FC<LiberacoesBannerProps> = ({
  liberacoes,
  selectedUnitId,
  darkMode,
  className,
}) => {
  if (!liberacoes || liberacoes.length === 0) return null;

  const unitCount = new Set(liberacoes.map((l) => l.unitId || l.unitLabel)).size;
  const coversSelected = !selectedUnitId || liberacoes.some((l) => l.unitId === selectedUnitId);

  return (
    <div
      className={cn(
        'rounded-xl border-2 shadow-lg overflow-hidden',
        darkMode
          ? 'bg-emerald-900/25 border-emerald-500 shadow-emerald-500/20'
          : 'bg-emerald-50 border-emerald-500 shadow-emerald-400/30',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2',
          darkMode ? 'bg-emerald-600/25' : 'bg-emerald-500/15',
        )}
      >
        <ShieldCheck className={cn('h-5 w-5 flex-shrink-0', darkMode ? 'text-emerald-300' : 'text-emerald-700')} />
        <span className={cn('text-sm font-extrabold uppercase tracking-wide', darkMode ? 'text-emerald-200' : 'text-emerald-800')}>
          Visitante já liberado
        </span>
        <span className={cn('ml-auto text-[11px] font-bold', darkMode ? 'text-emerald-300' : 'text-emerald-700')}>
          {unitCount === 1 ? '1 unidade' : `${unitCount} unidades`}
        </span>
      </div>

      {!coversSelected && (
        <div className={cn('flex items-start gap-2 px-3 py-2 text-xs font-semibold', darkMode ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-100 text-amber-800')}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>A liberação não cobre a unidade selecionada — siga o procedimento normal.</span>
        </div>
      )}

      <div className="p-2 space-y-2">
        {liberacoes.map((lib) => {
          const isSelected = !!selectedUnitId && lib.unitId === selectedUnitId;
          return (
            <div
              key={lib.roleId}
              className={cn(
                'rounded-lg border px-3 py-2',
                isSelected
                  ? darkMode
                    ? 'bg-emerald-800/40 border-emerald-400'
                    : 'bg-white border-emerald-500 ring-2 ring-emerald-400'
                  : darkMode
                    ? 'bg-gray-800/60 border-gray-700'
                    : 'bg-white border-emerald-200',
              )}
            >
              <div className="flex items-center gap-2 flex-wrap">
                {/* The rua leads when there is one — it is what the porteiro directs the visitor
                    by, and it used to be missing entirely: the banner read the frozen
                    `unit_display`, so a liberação on "Av. Carlos Gomes, 3" showed a bare "20 - 4",
                    or "Unidade não informada" beside a payload that knew the address. The
                    quadra/lote stays, one line down — both are needed, since every lote 4 in the
                    condomínio reads alike without the street. */}
                {lib.unitStreetLine ? (
                  <span className="min-w-0">
                    <span className={cn('block text-base font-extrabold leading-tight truncate', darkMode ? 'text-white' : 'text-gray-900')}>
                      {lib.unitStreetLine}
                    </span>
                    {lib.unitLabel && lib.unitLabel !== 'Unidade não informada' && (
                      <span className={cn('block text-xs font-semibold leading-tight', darkMode ? 'text-gray-300' : 'text-gray-600')}>
                        {lib.unitLabel}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className={cn('text-sm font-bold', darkMode ? 'text-white' : 'text-gray-900')}>
                    {lib.unitLabel}
                  </span>
                )}
                {isSelected && <Badge tone="green">Esta unidade</Badge>}
                {lib.isServiceProvider && <Badge tone="blue">Prestador</Badge>}
                <span className="ml-auto">
                  {lib.requiresIntercom ? (
                    <Badge tone="amber" icon={<Phone className="h-3 w-3" />}>Interfonar</Badge>
                  ) : (
                    <Badge tone="green" icon={<PhoneOff className="h-3 w-3" />}>Não interfonar</Badge>
                  )}
                </span>
              </div>

              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <Clock className={cn('h-3.5 w-3.5 flex-shrink-0', darkMode ? 'text-gray-400' : 'text-gray-500')} />
                <span className={cn('text-xs font-semibold', darkMode ? 'text-gray-200' : 'text-gray-800')}>
                  {lib.periodLabel}
                </span>
                {lib.rangeLabel && (
                  <span className={cn('text-xs', darkMode ? 'text-gray-400' : 'text-gray-600')}>{lib.rangeLabel}</span>
                )}
                {lib.authorizedByName && (
                  <span className={cn('text-xs', darkMode ? 'text-gray-500' : 'text-gray-500')}>
                    por {lib.authorizedByName}
                  </span>
                )}
                {lib.notStartedYet && <Badge tone="red">Ainda não válida</Badge>}
                {!lib.notStartedYet && lib.withinWindowNow === true && <Badge tone="green">No horário</Badge>}
                {!lib.notStartedYet && lib.withinWindowNow === false && <Badge tone="red">Fora do horário</Badge>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LiberacoesBanner;
